/**
 * Gap as score − target (me − benchmark or me − max).
 * Negative = behind target (bad); positive = at/above target.
 */
export function scoreGapFromTarget(
  score: number | null | undefined,
  target: number | null | undefined
): number | null {
  if (score == null || target == null) return null;
  return score - target;
}

/** @deprecated Prefer scoreGapFromTarget — kept as alias during rename. */
export const scoreGapToTarget = (
  target: number | null | undefined,
  score: number | null | undefined
): number | null => scoreGapFromTarget(score, target);

export function formatScore(value: number | null | undefined): string {
  return value != null ? value.toFixed(2) : '—';
}

/**
 * Scale-based σ when cohort SD is unavailable.
 * Continuous uniform on [0, scaleMax] → σ = scaleMax / √12.
 */
export function scaleSigma(scaleMax: number | null | undefined): number {
  if (scaleMax == null || scaleMax <= 0) return 1;
  return scaleMax / Math.sqrt(12);
}

/**
 * Per-question ceiling only. NPS may use 10 when unset.
 * Never invents a campaign/Likert max — returns null so gap is omitted.
 */
export function resolveItemScaleMax(
  questionType: string | null | undefined,
  questionScaleMax: number | null | undefined,
  _campaignScaleMax?: number
): number | null {
  if (questionScaleMax != null && questionScaleMax > 0) return questionScaleMax;
  if (questionType === 'nps') return 10;
  return null;
}

export type GapSeverityTone = 'ok' | 'warn' | 'alert' | 'strength';

/**
 * Gap severity vs 1σ:
 * - gap > +1σ → strength (green)
 * - within ±1σ including 0 → neutral
 * - −1σ ≤ gap < 0 → yellow (mild deficit)
 * - gap < −1σ → alert (red)
 *
 * Gap-to-max uses each item's own ceiling, so it is ≤ 0 when on-scale.
 */
export function gapSeverityTone(
  gap: number | null | undefined,
  sigma: number | null | undefined
): GapSeverityTone {
  if (gap == null) return 'ok';
  const s = sigma != null && sigma > 0 ? sigma : 1;
  if (gap > s) return 'strength';
  if (gap >= 0) return 'ok';
  if (Math.abs(gap) > s) return 'alert';
  return 'warn';
}

export function gapSeverityClass(
  gap: number | null | undefined,
  sigma: number | null | undefined
): string {
  const tone = gapSeverityTone(gap, sigma);
  if (tone === 'alert') return 'text-[var(--color-error)] font-semibold tabular-nums';
  if (tone === 'warn') return 'text-[var(--color-warning)] font-semibold tabular-nums';
  if (tone === 'strength') return 'text-[var(--color-success)] font-semibold tabular-nums';
  return 'text-[var(--color-surface-text)] tabular-nums';
}

export type RankedGapItem = {
  key: string;
  label: string;
  meta?: string;
  gap: number;
  score: number | null;
};

const DEFAULT_SUMMARY_LIMIT = 5;

/**
 * Pick strengths / weaknesses from scored gaps.
 * With `strictSigma`: strength = gap > +σ, weakness = gap < −σ.
 * Otherwise (gap-to-max): top / bottom by gap rank (relative highs and lows).
 */
export function pickStrengthsAndWeaknesses(
  items: RankedGapItem[],
  options?: { sigma?: number; strictSigma?: boolean; limit?: number }
): { strengths: RankedGapItem[]; weaknesses: RankedGapItem[] } {
  const limit = options?.limit ?? DEFAULT_SUMMARY_LIMIT;
  const scored = items.filter((i) => i.gap != null && Number.isFinite(i.gap));
  const sorted = [...scored].sort((a, b) => b.gap - a.gap);

  if (options?.strictSigma && options.sigma != null && options.sigma > 0) {
    const s = options.sigma;
    return {
      strengths: sorted.filter((i) => i.gap > s).slice(0, limit),
      weaknesses: [...sorted].filter((i) => i.gap < -s).slice(0, limit),
    };
  }

  // Relative: highest gaps = strengths, lowest = weaknesses; avoid overlap.
  const half = Math.max(1, Math.floor(sorted.length / 2));
  const strengthCap = Math.min(limit, half);
  const weaknessCap = Math.min(limit, Math.max(1, sorted.length - strengthCap));
  const strengths = sorted.slice(0, strengthCap);
  const strengthKeys = new Set(strengths.map((i) => i.key));
  const weaknesses = [...sorted]
    .reverse()
    .filter((i) => !strengthKeys.has(i.key))
    .slice(0, weaknessCap);

  return { strengths, weaknesses };
}
